// types
import { IOption } from 'components/FormElements/Select'
import { FieldSource, FieldType, IField, IInjuryMasterIncidentProperty, IRiskTemplate } from 'types/graphql'

export interface FieldOption {
  label: string
  value: string
  field?: IField
}

const FIELD_TYPE_LABELS: Partial<Record<FieldType, string>> = {
  [FieldType.Text]: 'Text',
  [FieldType.Number]: 'Number',
  [FieldType.Date]: 'Date',
  [FieldType.Switch]: 'Yes / No',
  [FieldType.PickList]: 'Pick list',
  [FieldType.Lookup]: 'Lookup'
}

const SOURCE_LABELS: Partial<Record<FieldSource, string>> = {
  [FieldSource.Users]: 'Users',
  [FieldSource.BusinessUnits]: 'Business Units',
  [FieldSource.Locations]: 'Locations'
}

// Properties Injury Master needs to create an incident are marked with an asterisk.
export const toPropertyOption = (property: IInjuryMasterIncidentProperty): IOption => ({
  value: property.property,
  label: property.requiredOnCreate ? `${property.label} *` : property.label
})

// A parent case transaction also reads the fields of the form's parent templates — those are
// labelled with their template's name, as a FROM_PARENT_FIELD value is (utils/form).
export const getFieldOptionLabel = (fields: IField[], field: IField, parentTemplates: IRiskTemplate[] = []): string => {
  const parent = field.parentFieldId ? fields.find(({ id }) => id === field.parentFieldId) : undefined
  const template = parentTemplates.find(({ id }) => id === field.entityId)
  const label = parent ? `${parent.label}: ${field.label}` : field.label

  return template ? `${template.name}: ${label}` : label
}

// The form's own fields, then the active fields of its parent templates.
export const getSourceFields = (fields: IField[], parentTemplates: IRiskTemplate[] = []): IField[] => {
  const parentFields = parentTemplates
    .flatMap((template) => template.fields ?? [])
    .filter((field) => field.active && !fields.some(({ id }) => id === field.id))

  return [...fields, ...parentFields]
}

export const toFieldOption = (fields: IField[], field: IField, parentTemplates: IRiskTemplate[] = []): FieldOption => ({
  label: getFieldOptionLabel(fields, field, parentTemplates),
  value: field.id,
  field
})

// A field can fill a property when its type is one the property reads from and, for a record
// lookup, its source is the right kind of record.
export const isFieldCompatible = (property: IInjuryMasterIncidentProperty | undefined, field: IField): boolean => {
  if (!property || !property.compatibleFieldTypes.includes(field.fieldType)) {
    return false
  }

  return !property.lookupSource || !!field.source?.includes(property.lookupSource)
}

// "Text, Number" or "Lookup of Users" — what the mapping UI tells the user a property fills from.
export const describeCompatibleFields = (property: IInjuryMasterIncidentProperty): string => {
  const types = property.compatibleFieldTypes.map((type) => FIELD_TYPE_LABELS[type] ?? type)

  if (property.lookupSource) {
    return `Lookup of ${SOURCE_LABELS[property.lookupSource] ?? property.lookupSource}`
  }

  return types.join(', ')
}
